import { useEffect, useCallback, useState, useRef } from 'react';
import { useAppStore, type ShowFortuneParams } from '@/hooks/use-app-store';

export const useFortuneDisplay = () => {
  const { showFortune, sub, unsub } = useAppStore();
  const [isAnimating, setIsAnimating] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio('/audio/fortune-reveal.m4a');
    audioRef.current.volume = 0.8; // Slightly lower than background music

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleSystemMessage = useCallback((payload?: any) => {
    if (!payload || typeof payload !== 'object') return;

    if (payload.data?.showFortune) {
      // Start animation first
      setIsAnimating(true);

      // Play fortune reveal sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0; // Reset to beginning
        audioRef.current.play().catch(error => {
          console.log('Fortune reveal sound play failed:', error);
        });
      }

      // Show fortune after animation delay
      setTimeout(() => {
        showFortune(payload.data.showFortune as ShowFortuneParams);
        setIsAnimating(false);
      }, 3000); // 2 second animation
    }
  }, [showFortune]);

  useEffect(() => {
    sub('system_event', handleSystemMessage);

    // Also listen for custom test events from the FortuneTestButton
    const handleTestEvent = (event: CustomEvent) => {
      if (event.detail?.type === 'test-fortune') {
        handleSystemMessage(event.detail.payload);
      }
    };

    window.addEventListener('test-fortune-trigger', handleTestEvent as EventListener);

    return () => {
      unsub('system_event', handleSystemMessage);
      window.removeEventListener('test-fortune-trigger', handleTestEvent as EventListener);
    };
  }, [sub, unsub, handleSystemMessage]);

  return {
    isAnimating
  };
};